// QuestionnaireBuilder — HR builds the questionnaire for a pipeline stage.
// Embedded inside PipelineBuilder when a stage of type 'questionnaire' is expanded.
import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from "react-toastify";
import api from "../../services/api";
const emptyQuestion = (type = "text") => ({
  _tempId: Date.now() + Math.random(),
  questionId: null,
  questionType: type,
  text: "",
  options: ["", "", "", ""],
  correctOption: null,
  keywords: [],
  points: 1,
});
export default function QuestionnaireBuilder({ jobId, stageId, stageLabel }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [kwInput, setKwInput] = useState({}); // { [idx]: string }
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/questionnaire/${jobId}/${stageId}`);
      setData(r.data?.data || { title: stageLabel || "Questionnaire", instructions: "", passingScore: 60, questions: [] });
    } catch {
      toast.error("Could not load questionnaire");
    } finally {
      setLoading(false);
    }
  }, [jobId, stageId, stageLabel]);
  useEffect(() => { load(); }, [load]);
  const update = (patch) => { setData(d => ({ ...d, ...patch })); setDirty(true); };
  const updateQ = (idx, patch) => {
    setData(d => {
      const qs = [...d.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...d, questions: qs };
    });
    setDirty(true);
  };
  const addQuestion = (type) => {
    setData(d => ({ ...d, questions: [...d.questions, emptyQuestion(type)] }));
    setDirty(true);
  };
  const removeQuestion = (idx) => {
    setData(d => ({ ...d, questions: d.questions.filter((_, i) => i !== idx) }));
    setDirty(true);
  };
  const addKeyword = (idx) => {
    const kw = (kwInput[idx] || "").trim();
    if (!kw) return;
    updateQ(idx, { keywords: [...(data.questions[idx].keywords || []), kw] });
    setKwInput(k => ({ ...k, [idx]: "" }));
  };
  const removeKeyword = (idx, ki) => {
    const kws = (data.questions[idx].keywords || []).filter((_, i) => i !== ki);
    updateQ(idx, { keywords: kws });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/questionnaire/${jobId}/${stageId}`, data);
      toast.success("Questionnaire saved");
      setDirty(false);
    } catch {
      toast.error("Failed to save questionnaire");
    } finally {
      setSaving(false);
    }
  };
  if (loading) return (
    <div className="flex items-center gap-2 text-gray-500 py-4 px-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading questionnaire…
    </div>
  );
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-2 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-blue-900 text-sm">Questionnaire Builder</h3>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="flex items-center gap-1.5 bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Title</label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={data.title}
            onChange={e => update({ title: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Passing Score (%)</label>
          <input
            type="number" min={0} max={100}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={data.passingScore}
            onChange={e => update({ passingScore: Number(e.target.value) })}
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs text-gray-500 mb-1">Instructions for candidates</label>
          <textarea
            rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={data.instructions}
            placeholder="e.g. Please answer honestly. There are no right or wrong answers for text questions."
            onChange={e => update({ instructions: e.target.value })}
          />
        </div>
      </div>
      {/* Questions */}
      <div className="space-y-3">
        {data.questions.map((q, idx) => (
          <div key={q.questionId || q._tempId} className="bg-white border rounded-xl p-4 space-y-3">
            {/* Question header */}
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-semibold text-gray-400 mt-1">Q{idx + 1}</span>
              <div className="flex-1">
                <textarea
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Type your question here…"
                  value={q.text}
                  onChange={e => updateQ(idx, { text: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  className="border rounded-lg px-2 py-1.5 text-xs"
                  value={q.questionType}
                  onChange={e => updateQ(idx, { questionType: e.target.value, correctOption: null, keywords: [] })}
                >
                  <option value="text">Text answer</option>
                  <option value="mcq">Multiple choice</option>
                </select>
                <input
                  type="number" min={1} max={10}
                  className="border rounded-lg px-2 py-1.5 text-xs w-16"
                  title="Points"
                  value={q.points}
                  onChange={e => updateQ(idx, { points: Number(e.target.value) })}
                />
                <button onClick={() => removeQuestion(idx)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* MCQ options */}
            {q.questionType === "mcq" && (
              <div className="space-y-1.5 pl-4">
                <p className="text-xs text-gray-500 mb-1">Options — click the radio to mark the correct answer</p>
                {(q.options || ["", "", "", ""]).map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${idx}`}
                      checked={q.correctOption === oi}
                      onChange={() => updateQ(idx, { correctOption: oi })}
                      className="accent-blue-600"
                    />
                    <input
                      className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
                      placeholder={`Option ${oi + 1}`}
                      value={opt}
                      onChange={e => {
                        const opts = [...(q.options || [])];
                        opts[oi] = e.target.value;
                        updateQ(idx, { options: opts });
                      }}
                    />
                  </div>
                ))}
                {q.correctOption === null && (
                  <p className="text-xs text-amber-500">Select the correct option by clicking a radio button</p>
                )}
              </div>
            )}
            {/* Text question keywords */}
            {q.questionType === "text" && (
              <div className="pl-4 space-y-1.5">
                <p className="text-xs text-gray-500">
                  Keywords to look for in the answer (partial match, case-insensitive).
                  Leave empty to give full points to everyone and review manually.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(q.keywords || []).map((kw, ki) => (
                    <span key={ki} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                      {kw}
                      <button onClick={() => removeKeyword(idx, ki)} className="text-blue-400 hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    className="border rounded-lg px-3 py-1.5 text-sm flex-1"
                    placeholder="Add keyword (press Enter)"
                    value={kwInput[idx] || ""}
                    onChange={e => setKwInput(k => ({ ...k, [idx]: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKeyword(idx); } }}
                  />
                  <button
                    onClick={() => addKeyword(idx)}
                    className="bg-blue-100 text-blue-700 text-xs px-3 py-1.5 rounded-lg hover:bg-blue-200"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Add question buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => addQuestion("text")}
          className="flex items-center gap-1.5 bg-white border border-blue-300 text-blue-700 text-xs px-3 py-2 rounded-lg hover:bg-blue-50"
        >
          <Plus className="w-3 h-3" /> Add Text Question
        </button>
        <button
          onClick={() => addQuestion("mcq")}
          className="flex items-center gap-1.5 bg-white border border-blue-300 text-blue-700 text-xs px-3 py-2 rounded-lg hover:bg-blue-50"
        >
          <Plus className="w-3 h-3" /> Add MCQ
        </button>
      </div>
      {data.questions.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">No questions yet. Add text or MCQ questions above.</p>
      )}
    </div>
  );
}
